import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posBackendApi } from '@/lib/api-client';

export type CampaignChannel = 'email' | 'sms';
export type CampaignStatus =
  | 'draft'
  | 'scheduled'
  | 'sending'
  | 'in_progress'
  | 'sent'
  | 'paused'
  | 'failed'
  | 'cancelled';

export type CampaignAudienceType = 'all' | 'segment' | 'manual_list';

export interface Campaign {
  id: string;
  tenantId: string;
  name: string;
  channel: CampaignChannel;
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  audienceType: CampaignAudienceType;
  segmentId: string | null;
  attachedCouponId: string | null;
  status: CampaignStatus;
  sendAt: string | null;
  recurrenceRule: string | null;
  locationId: string | null;
  scheduledAt: string | null;
  targetFinishAt: string | null;
  targetCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  startedAt: string | null;
  completedAt: string | null;
  senderAddress: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignMetrics {
  total: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  failed: number;
}

export interface CampaignStatusSummary {
  id: string;
  status: CampaignStatus;
  targetCount: number;
  sentCount: number;
  failedCount: number;
  skippedCount: number;
  pendingCount: number;
  scheduledAt: string | null;
  targetFinishAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  lastActivityAt: string | null;
  estimatedCompletionAt: string | null;
  senderAddress: string | null;
}

export interface CreateCampaignInput {
  name: string;
  channel: CampaignChannel;
  subject?: string;
  bodyHtml?: string;
  bodyText?: string;
  audienceType?: CampaignAudienceType;
  segmentId?: string;
  attachedCouponId?: string;
  locationId?: string;
}

export interface UploadRecipientInput {
  phone: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export interface UploadResult {
  received: number;
  matched: number;
  enrolled: number;
  skipped: {
    duplicate: number;
    invalidPhone: number;
    optOut: number;
  };
  totalRecipients: number;
}

export interface ScheduleCampaignInput {
  scheduledAt: string;
  targetFinishAt: string;
}

const KEY = ['customer-engagement', 'campaigns'] as const;

export function useCampaigns() {
  return useQuery({
    queryKey: KEY,
    queryFn: () => posBackendApi.get<{ data: Campaign[]; meta: { count: number } }>('/customer-engagement/campaigns'),
    staleTime: 30_000,
  });
}

export function useCampaign(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => posBackendApi.get<{ data: Campaign }>(`/customer-engagement/campaigns/${id}`),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useCampaignMetrics(id: string | undefined) {
  return useQuery({
    queryKey: [...KEY, 'metrics', id],
    queryFn: () => posBackendApi.get<{ data: CampaignMetrics }>(`/customer-engagement/campaigns/${id}/metrics`),
    enabled: !!id,
    staleTime: 10_000,
  });
}

export function useCampaignStatus(id: string | undefined, opts: { refetchMs?: number } = {}) {
  return useQuery({
    queryKey: [...KEY, 'status', id],
    queryFn: () => posBackendApi.get<{ data: CampaignStatusSummary }>(`/customer-engagement/campaigns/${id}/status`),
    enabled: !!id,
    refetchInterval: opts.refetchMs ?? false,
    refetchIntervalInBackground: false,
  });
}

export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCampaignInput) =>
      posBackendApi.post<{ data: Campaign }>('/customer-engagement/campaigns', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUploadCampaignRecipients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, recipients }: { id: string; recipients: UploadRecipientInput[] }) =>
      posBackendApi.post<{ data: UploadResult }>(`/customer-engagement/campaigns/${id}/recipients`, {
        recipients,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', vars.id] });
      qc.invalidateQueries({ queryKey: [...KEY, 'status', vars.id] });
    },
  });
}

export function useScheduleCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scheduledAt, targetFinishAt }: { id: string } & ScheduleCampaignInput) =>
      posBackendApi.post<{ data: Campaign }>(`/customer-engagement/campaigns/${id}/schedule`, {
        scheduledAt,
        targetFinishAt,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', vars.id] });
      qc.invalidateQueries({ queryKey: [...KEY, 'status', vars.id] });
    },
  });
}

export function useCancelCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => posBackendApi.post<{ data: Campaign }>(`/customer-engagement/campaigns/${id}/cancel`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
      qc.invalidateQueries({ queryKey: [...KEY, 'status', id] });
    },
  });
}

export function useSendCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      posBackendApi.post<{ data: { sent: number; failed: number; total: number } }>(
        `/customer-engagement/campaigns/${id}/send-now`,
      ),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: [...KEY, 'metrics', id] });
    },
  });
}

export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      posBackendApi.delete<{ data: { id: string; name: string; status: string } }>(
        `/customer-engagement/campaigns/${id}`,
      ),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: KEY });
      qc.invalidateQueries({ queryKey: [...KEY, 'detail', id] });
      qc.invalidateQueries({ queryKey: [...KEY, 'status', id] });
    },
  });
}
